import { useState } from 'react';

import { ListItem, ListItemButton, Stack, ListItemIcon, ListItemText, List, Typography, Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { RecoilState } from 'recoil';
import { useRecoilValue } from 'recoil';

import { Button, Checkbox, DrawerContainer } from '../../../../components/base';
import useDrawer from '../../../../hooks/useDrawer';
import { currentEventSettings } from '../../../../state/event';
import type { ColumnsSettings, ColumnValue } from '../../state';

type GridColumnsSettingsDrawerProps<T extends string> = {
  setColumns: (newColumns: ColumnsSettings<T>) => void;
  columnsState: RecoilState<ColumnsSettings<T>>;
  columnsNameTexts: Record<T, string>;
};

export const GridColumnsSettingsDrawer = <T extends string>({
  setColumns,
  columnsState,
  columnsNameTexts,
}: GridColumnsSettingsDrawerProps<T>) => {
  const { t } = useTranslation();
  const columns = useRecoilValue(columnsState);
  const [currentColumns, setCurrentColumns] = useState(columns);
  const { availableIncidentPlan } = useRecoilValue(currentEventSettings);
  const { closeDrawer } = useDrawer();

  const setColumnHandler = (field: T) => {
    setCurrentColumns((old) => ({
      ...old,
      [field]: { ...old[field], isShow: !old[field].isShow },
    }));
  };

  const updateColumnsStateHandler = () => {
    setColumns(currentColumns);
    closeDrawer();
  };

  const handleSelectAll = () => {
    setCurrentColumns((prev) => {
      const newColumns = { ...prev };

      (Object.keys(newColumns) as (keyof typeof newColumns)[]).forEach((key) => {
        newColumns[key] = { ...newColumns[key], isShow: true };
      });

      return newColumns;
    });
  };

  const handleDeselectAll = () => {
    setCurrentColumns((prev) => {
      const newColumns = { ...prev };

      (Object.keys(newColumns) as (keyof typeof newColumns)[]).forEach((key) => {
        if (newColumns[key].required) return;

        newColumns[key] = { ...newColumns[key], isShow: false };
      });

      return newColumns;
    });
  };

  return (
    <DrawerContainer>
      <Stack spacing={2}>
        <Typography variant='h4'>
          <Trans i18nKey='columns' />
        </Typography>

        <Box sx={{ display: 'flex' }}>
          <Button onClick={handleSelectAll}>{t('select-all')}</Button>
          <Button onClick={handleDeselectAll}>{t('deselect-all')}</Button>
        </Box>

        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {Object.entries(currentColumns)
            .filter((item) => {
              if (item[0] === 'relation' && !availableIncidentPlan) {
                return false;
              }
              return true;
            })
            .map(([field, props]) => {
              const columnSettings = props as ColumnValue;
              const column = field as T;
              const fieldText = columnsNameTexts[column];

              return (
                <ListItem key={field} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={() => setColumnHandler(field as T)}
                    dense
                    disabled={columnSettings.required}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge='start'
                        checked={columnSettings.isShow}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': field }}
                      />
                    </ListItemIcon>
                    <ListItemText id={field} primary={fieldText} />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Stack>

      <Button sx={{ marginTop: 'auto', color: 'white' }} variant='contained' onClick={updateColumnsStateHandler}>
        <Trans i18nKey='apply-settings' />
      </Button>
    </DrawerContainer>
  );
};
